import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { catchError, tap } from 'rxjs';
import {
  GetUserPreferences,
  UpdateUserPreferences,
  ClearUserPreferences,
} from './user-preferences.actions';
import { UserPreferencesService } from './user-preferences.service';
import { UserPreferencesStateModel, UserPreferencesModel } from './user-preferences.types';

const defaults: UserPreferencesStateModel = {
  preferences: null,
  loading: false,
  error: null,
};

@State<UserPreferencesStateModel>({
  name: 'userPreferences',
  defaults,
})
@Injectable()
export class UserPreferencesState {
  userPreferencesService: UserPreferencesService = inject(UserPreferencesService);

  @Selector()
  static getPreferences(state: UserPreferencesStateModel): UserPreferencesModel | null {
    return state.preferences;
  }

  @Selector()
  static getLoading(state: UserPreferencesStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static getError(state: UserPreferencesStateModel): string | null {
    return state.error;
  }

  @Action(GetUserPreferences)
  getPreferences(ctx: StateContext<UserPreferencesStateModel>) {
    ctx.patchState({ loading: true });

    return this.userPreferencesService.getPreferences().pipe(
      tap((preferences) => {
        ctx.patchState({
          preferences,
          loading: false,
          error: null,
        });
      }),
      catchError((err) => {
        ctx.patchState({
          loading: false,
          error: err.message || 'Failed to load preferences',
        });
        throw err;
      })
    );
  }

  @Action(UpdateUserPreferences)
  updatePreferences(
    ctx: StateContext<UserPreferencesStateModel>,
    action: UpdateUserPreferences
  ) {
    ctx.patchState({ loading: true });

    return this.userPreferencesService.updatePreferences(action.dto).pipe(
      tap((preferences) => {
        ctx.patchState({
          preferences,
          loading: false,
          error: null,
        });
      }),
      catchError((err) => {
        ctx.patchState({
          loading: false,
          error: err.message || 'Failed to update preferences',
        });
        throw err;
      })
    );
  }

  @Action(ClearUserPreferences)
  clearPreferences(ctx: StateContext<UserPreferencesStateModel>) {
    ctx.setState(defaults);
  }
}
